import { FormattedMessage } from 'react-intl';
import { ReactNode } from 'react';
import CalendarBadgeIcon from '@/assets/calendar-badge.svg?react';
import RtpBadgeIcon from '@/assets/rtp-badge.svg?react';
import VolatilityBadgeIcon from '@/assets/volatility-badge.svg?react';
import MinMaxBetBadgeIcon from '@/assets/minMaxBet-badge.svg?react';
import BuyFeatureBadgeIcon from '@/assets/buy-feature-badge.svg?react';
import StatusBadgeIcon from '@/assets/status-badge.svg?react';
import { GameSchema } from '@/lib/types';
import dayjs from 'dayjs';
import { Volatilities } from './Volatilities';

export const DASH = '—';

const formatValue = <T,>(
  value: T | null | undefined,
  formatter?: (value: T) => string,
): string => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'number' && Number.isNaN(value))
  ) {
    return DASH;
  }
  return formatter ? formatter(value) : String(value);
};

export type GameAttributeSection =
  | 'overview'
  | 'gameSetup'
  | 'mathInformation'
  | 'featuresSupport';

export type GameAttributeVariant =
  | 'text'
  | 'badgeSupport'
  | 'badgeYesNo'
  | 'badgeStatus';

export type GameAttributeConfig = {
  label: ReactNode;
  section: GameAttributeSection;
  icon?: ReactNode;
  variant?: GameAttributeVariant;
  parseValue: (game: GameSchema) => string | boolean | null | undefined;
};

export const GameAttributes: Record<string, GameAttributeConfig> = {
  // Overview
  release_date: {
    label: (
      <FormattedMessage
        id="releaseDate"
        defaultMessage="Release Date"
      />
    ),
    section: 'overview',
    icon: <CalendarBadgeIcon />,
    parseValue: (game) =>
      formatValue(game.release_date, (value) =>
        dayjs(value).format('YYYY MMM DD'),
      ),
  },
  rtp: {
    label: (
      <FormattedMessage id="defaultRtp" defaultMessage="Default RTP" />
    ),
    section: 'overview',
    icon: <RtpBadgeIcon />,
    parseValue: (game) =>
      formatValue(game.rtp, (value) => `${Number(value).toFixed(2)}%`),
  },
  volatility: {
    label: (
      <FormattedMessage id="volatility" defaultMessage="Volatility" />
    ),
    section: 'overview',
    icon: <VolatilityBadgeIcon />,
    parseValue: (game) =>
      formatValue(
        game.volatility,
        (value) =>
          Volatilities.find(({ value: v }) => v[0] === Number(value))
            ?.name || value,
      ),
  },
  min_max_bet: {
    label: (
      <FormattedMessage
        id="minMaxBet"
        defaultMessage="Min | Max Bet"
      />
    ),
    section: 'overview',
    icon: <MinMaxBetBadgeIcon />,
    parseValue: (game) =>
      game.min_bet == null || game.max_bet == null
        ? DASH
        : `${game.min_bet} | ${game.max_bet}`,
  },
  buy_feature: {
    label: (
      <FormattedMessage
        id="buyFeature"
        defaultMessage="Buy Feature"
      />
    ),
    section: 'overview',
    icon: <BuyFeatureBadgeIcon />,
    variant: 'badgeSupport',
    parseValue: (game) => game.buy_feature,
  },
  status: {
    label: <FormattedMessage id="status" defaultMessage="Status" />,
    section: 'overview',
    icon: <StatusBadgeIcon />,
    variant: 'badgeStatus',
    parseValue: (game) => game.status,
  },

  // Game Setup
  studio_game_id: {
    label: <FormattedMessage id="gameId" defaultMessage="Game ID" />,
    section: 'gameSetup',
    parseValue: (game) => formatValue(game.studio_game_id),
  },
  game_family: {
    label: (
      <FormattedMessage
        id="gameFamily"
        defaultMessage="Game Family"
      />
    ),
    section: 'gameSetup',
    parseValue: (game) => formatValue(game.game_family),
  },
  line_config: {
    label: (
      <FormattedMessage
        id="lineConfiguration"
        defaultMessage="Line Configuration"
      />
    ),
    section: 'gameSetup',
    parseValue: (game) => formatValue(game.line_config),
  },
  reel_array: {
    label: (
      <FormattedMessage id="reelArray" defaultMessage="Reel Array" />
    ),
    section: 'gameSetup',
    parseValue: (game) => formatValue(game.reel_array),
  },
  wager_type: {
    label: (
      <FormattedMessage id="wagerType" defaultMessage="Wager Type" />
    ),
    section: 'gameSetup',
    parseValue: (game) => formatValue(game.wager_type),
  },
  default_bet: {
    label: (
      <FormattedMessage id="defaultBet" defaultMessage="Default Bet" />
    ),
    section: 'gameSetup',
    parseValue: (game) => formatValue(game.default_bet),
  },
  min_technical_bet: {
    label: (
      <FormattedMessage
        id="minTechnicalBet"
        defaultMessage="Minimum Technical Bet"
      />
    ),
    section: 'gameSetup',
    parseValue: (game) =>
      formatValue(game.min_technical_bet, (value) =>
        Number(value).toFixed(2),
      ),
  },

  // Math Information
  buy_feature_rtp: {
    label: (
      <FormattedMessage
        id="buyFeatureRtp"
        defaultMessage="Buy Feature RTP"
      />
    ),
    section: 'mathInformation',
    parseValue: (game) =>
      formatValue(
        game.buy_feature_rtp,
        (value) => `${Number(value).toFixed(2)}%`,
      ),
  },
  buy_feature_max_win_multiplier: {
    label: (
      <FormattedMessage
        id="bfMaxWinMultiplier"
        defaultMessage="BF Max Win Multiplier"
      />
    ),
    section: 'mathInformation',
    parseValue: (game) =>
      formatValue(
        game.buy_feature_max_win_multiplier,
        (value) => `${Number(value).toLocaleString()}x`,
      ),
  },

  // Features & Support
  free_rounds_support: {
    label: (
      <FormattedMessage
        id="freeRounds"
        defaultMessage="Free Rounds"
      />
    ),
    section: 'featuresSupport',
    variant: 'badgeSupport',
    parseValue: (game) => game.free_rounds_support,
  },
  free_rounds_bonus_support: {
    label: (
      <FormattedMessage
        id="freeRoundsBonus"
        defaultMessage="Free Rounds Bonus"
      />
    ),
    section: 'featuresSupport',
    variant: 'badgeSupport',
    parseValue: (game) => game.free_rounds_bonus_support,
  },
  awarded_feature_support: {
    label: (
      <FormattedMessage
        id="awardedFeature"
        defaultMessage="Awarded Feature"
      />
    ),
    section: 'featuresSupport',
    variant: 'badgeSupport',
    parseValue: (game) => game.awarded_feature_support,
  },
  in_game_jackpot: {
    label: (
      <FormattedMessage
        id="inGameJackpot"
        defaultMessage="In-Game Jackpot"
      />
    ),
    section: 'featuresSupport',
    variant: 'badgeYesNo',
    parseValue: (game) => game.in_game_jackpot,
  },
  in_game_progress: {
    label: (
      <FormattedMessage
        id="inGameProgress"
        defaultMessage="In Game Progress"
      />
    ),
    section: 'featuresSupport',
    variant: 'badgeYesNo',
    parseValue: (game) => game.in_game_progress,
  },
};
